import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchWord, fetchAtom, fetchProgress, fetchWordProgress } from "@/lib/api";
import { compoundUnlockState } from "@/lib/unlock";
import UnlockBadge from "@/components/UnlockBadge";
import JlptBadge, { JLPT_COLOR } from "@/components/JlptBadge";
import ProgressButtons from "@/components/ProgressButtons";
import PrereqSuggestions from "@/components/PrereqSuggestions";

const TABS = [
  { id: "card",     label: "Card",     testId: "vocab-tab-card" },
  { id: "build",    label: "Build",    testId: "vocab-tab-build" },
  { id: "readings", label: "Readings", testId: "vocab-tab-readings" },
];

// Hoisted motion variants — declared once instead of recreated on every
// render (the previous inline objects caused unnecessary re-renders in
// framer-motion's reconciler).
const TRANSITION = { duration: 0.2 };
const FADE_DOWN = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: 6 },
};
const FADE_SLIDE = {
  initial: { opacity: 0, x: -8 },
  animate: { opacity: 1, x: 0 },
  exit:    { opacity: 0, x: 8 },
};

function PartLeaf({ part, onSelect }) {
  const acc = part.is_kanji && part.jlpt ? JLPT_COLOR[part.jlpt] : "#2A4B6A";
  const label = part.is_kanji
    ? part.meaning
    : (part.meaning ? (part.meaning.toLowerCase().includes("radical") ? part.meaning : `${part.meaning} radical`) : `${part.char} radical`);
  return (
    <button
      data-testid={`vocab-part-${part.char}`}
      onClick={() => part.is_kanji && onSelect?.(part.char)}
      className={`flex flex-col items-center px-2 py-1.5 border bg-washi transition-colors ${part.is_kanji ? "hover:border-shu cursor-pointer" : "cursor-default"}`}
      style={{ borderColor: acc }}
    >
      <span className="font-serif text-2xl leading-none" style={!part.is_kanji ? { color: acc } : undefined}>
        {part.char}
      </span>
      <span className="font-sans text-[9px] uppercase tracking-widest text-sumiSoft mt-1 max-w-[100px] truncate">
        {label}
      </span>
    </button>
  );
}

function KanjiBranch({ k, onSelect }) {
  const atomQ = useQuery({
    queryKey: ["atom", k.char],
    queryFn: () => fetchAtom(k.char, 3),
    enabled: !!k.char,
  });
  const acc = k.jlpt ? JLPT_COLOR[k.jlpt] : "#1E1B18";
  const atom = atomQ.data?.root;
  const sub = atom?.children || [];

  return (
    <div className="flex flex-col items-center gap-2 min-w-0">
      <button
        data-testid={`vocab-build-${k.char}`}
        onClick={() => onSelect?.(k.char)}
        className="flex flex-col items-center px-3 py-2 border bg-washi hover:border-shu transition-colors"
        style={{ borderColor: acc, borderWidth: 1 }}
      >
        <span className="font-serif text-4xl leading-none">{k.char}</span>
        <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft mt-1">
          {k.meaning || "—"}
        </span>
        {k.on_used && (
          <span className="font-serif text-xs text-shu mt-1">{k.on_used}</span>
        )}
      </button>
      {sub.length > 0 && (
        <>
          <span className="w-px h-3 bg-borderDefault" />
          <div className="flex flex-wrap gap-2 justify-center">
            {sub.map((p, i) => (
              <PartLeaf key={p.char + i} part={p} onSelect={onSelect} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Card tab ──────────────────────────────────────────────────────────────
function CardView({ w, unlock }) {
  return (
    <motion.div
      key={w.word + "-card"}
      {...FADE_DOWN}
      transition={TRANSITION}
      className="flex flex-col items-center bg-washiPanel border border-borderDefault px-6 py-8 mx-3 md:mx-8 my-4"
    >
      <span className="font-serif text-6xl md:text-7xl text-sumi leading-none">{w.word}</span>
      <p className="font-serif text-xl md:text-2xl text-sumiSoft mt-3">{w.reading}</p>
      <p className="font-serif text-2xl md:text-3xl text-sumi mt-3 text-center">{w.meaning}</p>
      <div className="mt-3 flex items-center gap-2 flex-wrap justify-center">
        <JlptBadge level={w.jlpt} size="md" />
        {unlock && unlock.total > 0 && (
          <UnlockBadge state={unlock.state} known={unlock.known} total={unlock.total} variant="chip" />
        )}
      </div>
      {/* Status pill-buttons — same look as kanji status buttons */}
      <div className="mt-5 w-full max-w-sm">
        <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-2 text-center">
          your progress
        </p>
        <ProgressButtons kind="word" id={w.word} testIdPrefix="vocab-progress" />
        <PrereqSuggestions constituents={w.constituents} />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {w.constituents.map((c, i) => (
          <span
            key={`${c.char}-${i}`}
            className="flex flex-col items-center px-2 py-1 border border-borderDefault bg-washi/60"
          >
            <span className="font-serif text-2xl text-sumi">{c.char}</span>
            <span className="font-sans text-[9px] uppercase tracking-widest text-sumiSoft mt-0.5">
              {c.is_kanji ? (c.meaning || "—") : "kana"}
            </span>
          </span>
        ))}
      </div>
    </motion.div>
  );
}

// ── Build tab ─────────────────────────────────────────────────────────────
function BuildView({ w, goKanji }) {
  return (
    <motion.div
      key={w.word + "-build"}
      {...FADE_SLIDE}
      transition={TRANSITION}
      className="px-3 md:px-8 py-4"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans">built from</span>
        <span className="hairline h-px flex-1 bg-borderDefault" />
      </div>
      <div className="flex flex-wrap items-start justify-center gap-6 md:gap-10 overflow-x-auto pb-4">
        {w.constituents.map((k, i) =>
          k.is_kanji
            ? <KanjiBranch key={`${k.char}-${i}`} k={k} onSelect={goKanji} />
            : <span key={`${k.char}-${i}`} className="font-serif text-3xl text-sumi self-center">{k.char}</span>
        )}
      </div>
      <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft text-center mt-8">
        tap a kanji to open its full card · build · phonetic view
      </p>
    </motion.div>
  );
}

// ── Readings tab — how the compound's sound is built from each kanji ─────
function ReadingsView({ w, goKanji }) {
  const isJukujikun = w.constituents.some((c) => c.is_kanji && c.jukujikun);
  return (
    <motion.div
      key={w.word + "-readings"}
      {...FADE_SLIDE}
      transition={TRANSITION}
      className="px-3 md:px-8 py-4"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans">how it&apos;s read</span>
        <span className="hairline h-px flex-1 bg-borderDefault" />
      </div>

      {isJukujikun && (
        <div
          data-testid="vocab-jukujikun-banner"
          className="mb-4 border border-shu/40 bg-shu/5 px-3 py-2 text-center"
        >
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-shu">
            jukujikun · 熟字訓
          </p>
          <p className="font-serif text-3xl text-sumi mt-1">{w.reading}</p>
          <p className="font-sans text-xs text-sumiSoft mt-1 italic">
            This is a special whole-word reading — it doesn&apos;t break down kanji-by-kanji.
          </p>
        </div>
      )}

      {!isJukujikun && (
        /* full reading with per-kanji segments highlighted */
        <div data-testid="vocab-reading-segments" className="flex flex-wrap items-baseline justify-center gap-2 mb-6 bg-washiPanel border border-borderDefault px-3 py-4">
          {w.constituents.map((c, i) => {
            const acc = c.is_kanji && c.jlpt ? JLPT_COLOR[c.jlpt] : "#7A746D";
            return (
              <span key={`${c.char}-${i}`} className="flex flex-col items-center">
                <span
                  className="font-serif text-3xl md:text-4xl"
                  style={{ color: acc }}
                >
                  {c.kana_used || c.char}
                </span>
                <span className="font-serif text-base text-sumiSoft mt-1">{c.char}</span>
              </span>
            );
          })}
        </div>
      )}

      {/* per-kanji explanation row */}
      <div className="grid gap-3 sm:grid-cols-2">
        {w.constituents.map((c, i) => {
          if (!c.is_kanji) {
            return (
              <div key={`${c.char}-${i}`} className="border border-borderDefault bg-washi/60 p-3">
                <div className="flex items-center gap-3">
                  <span className="font-serif text-3xl text-sumi">{c.char}</span>
                  <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">kana</span>
                </div>
              </div>
            );
          }
          const acc = c.jlpt ? JLPT_COLOR[c.jlpt] : "#1E1B18";
          const variant = c.on_used && c.kana_used && c.kana_used !== c.on_used;
          return (
            <button
              key={`${c.char}-${i}`}
              data-testid={`vocab-reading-${c.char}`}
              onClick={() => goKanji?.(c.char)}
              className="text-left border border-borderDefault hover:border-shu bg-washi/60 p-3 transition-colors"
              style={{ borderLeftColor: acc, borderLeftWidth: 3 }}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-3xl text-sumi">{c.char}</span>
                <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft truncate">
                  {c.meaning || "—"}
                </span>
              </div>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                {c.kana_used && (
                  <span className="font-serif text-2xl" style={{ color: acc }}>
                    {c.kana_used}
                  </span>
                )}
                {c.on_used && (
                  <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                    on · <span className="font-serif text-sm text-sumi normal-case tracking-normal">{c.on_used}</span>
                  </span>
                )}
                {!c.on_used && c.kun_used && (
                  <span className="font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                    kun · <span className="font-serif text-sm text-sumi normal-case tracking-normal">{c.kun_used}</span>
                  </span>
                )}
                {variant && (
                  <span className="font-sans text-[9px] uppercase tracking-widest text-shu border border-shu/40 px-1.5 py-0.5">
                    {soundChange(c.on_used, c.kana_used)}
                  </span>
                )}
              </div>
              {c.phonetic && (
                <p className="mt-2 font-sans text-[10px] uppercase tracking-widest text-sumiSoft">
                  phonetic · <span className="font-serif text-base text-shu normal-case tracking-normal">{c.phonetic.char}</span>
                  {c.phonetic.meaning && (
                    <span className="ml-1 normal-case tracking-normal">({c.phonetic.meaning})</span>
                  )}
                </p>
              )}
            </button>
          );
        })}
      </div>
      <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft text-center mt-8">
        tap a kanji to open its full card
      </p>
    </motion.div>
  );
}

function soundChange(onUsed, kanaUsed) {
  if (!onUsed || !kanaUsed) return "";
  if (kanaUsed.endsWith("っ")) return "sokuon";
  const a = onUsed[0];
  const b = kanaUsed[0];
  if (a === b) return "";
  const handaku = { は: "ぱ", ひ: "ぴ", ふ: "ぷ", へ: "ぺ", ほ: "ぽ" };
  if (handaku[a] === b) return "handaku";
  return "rendaku";
}

export default function VocabFocus() {
  const { word } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("card");
  const q = useQuery({ queryKey: ["word", word], queryFn: () => fetchWord(word), enabled: !!word });
  const progQ = useQuery({ queryKey: ["progress"], queryFn: fetchProgress });
  const goKanji = (ch) => navigate(`/kanji/${encodeURIComponent(ch)}`);

  if (!q.data) {
    return (
      <div className="p-8 text-center" data-testid="vocab-focus">
        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumiSoft animate-pulse">
          {q.isLoading ? "loading…" : "not found"}
        </p>
      </div>
    );
  }
  const w = q.data;
  const unlock = compoundUnlockState(
    w.constituents.filter((c) => c.is_kanji).map((c) => c.char),
    progQ.data?.items,
  );

  return (
    <div data-testid="vocab-focus" className="flex flex-col">
      {/* sub-header: back · tabs · word — mirrors KanjiFocus */}
      <div className="flex items-center justify-between gap-2 px-3 md:px-8 py-2 border-b border-borderDefault bg-washi/95 backdrop-blur-sm sticky top-[52px] md:top-[72px] z-20">
        <button
          data-testid="vocab-back"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] font-sans text-sumi hover:text-shu transition-colors px-2 py-1 shrink-0"
        >
          <ArrowLeft size={14} /> back
        </button>

        <nav className="flex items-center border border-borderDefault overflow-x-auto">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={t.testId}
                onClick={() => setTab(t.id)}
                className={`px-3 md:px-5 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] font-sans border-l first:border-l-0 border-borderDefault transition-colors whitespace-nowrap ${
                  active ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <p className="font-serif text-base md:text-lg text-sumi shrink-0 truncate max-w-[6rem] md:max-w-none flex items-center gap-2">
          <UnlockBadge state={unlock.state} known={unlock.known} total={unlock.total} variant="dot" />
          {w.word}
        </p>
      </div>

      <AnimatePresence mode="wait">
        {tab === "card"     && <CardView     key="c" w={w} unlock={unlock} />}
        {tab === "build"    && <BuildView    key="b" w={w} goKanji={goKanji} />}
        {tab === "readings" && <ReadingsView key="r" w={w} goKanji={goKanji} />}
      </AnimatePresence>
    </div>
  );
}
